import { DropzoneOptions } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Button, Card, Checkbox, cn } from '@sib/ui';
import { useUploadingFilesList } from '@spendbrain/common';
import { SortIcon, TrashIcon } from '@spendbrain/icons';
import { FileUploadStore } from '@spendbrain/stores/fileUploadStore';
import { getFileCounts } from '@spendbrain/utils/files';
import { FileUploadItem } from '../FileManager/FileUploadItem/FileUploadItem';

interface UploadingFilesListProps {
  fileUploadStore: FileUploadStore;
  fileListClassName?: string;
  dropzoneConfig?: Partial<DropzoneOptions>;
}

export const UploadingFilesList = ({
  fileListClassName,
  fileUploadStore,
  dropzoneConfig,
}: UploadingFilesListProps) => {
  const { t } = useTranslation();

  const {
    sortedFiles,
    areFilesUploading,
    areThereAnyFiles,
    allSelected,
    anySelected,
    files,
    removeFile,
    toggleSortOrder,
    updateFile,
    toggleSelectAllFiles,
  } = useUploadingFilesList({ fileUploadStore, dropzoneConfig });

  const { deleteSelectedFiles } = fileUploadStore;

  return (
    <Card className="h-full flex-1 overflow-hidden p-3 sm:p-[22px]">
      <div className="flex h-full flex-col space-y-4">
        <div className="mb-4 flex items-center justify-between pl-4">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={allSelected}
              onCheckedChange={toggleSelectAllFiles}
              disabled={areFilesUploading || !areThereAnyFiles}
            />
            <Button
              variant="ghost"
              type="button"
              className="px-2 text-[#667080] sm:p-4"
              onClick={toggleSelectAllFiles}
            >
              {t('selectAll')}
            </Button>
            <Button
              variant="ghost"
              type="button"
              className="px-2.5 sm:p-4"
              onClick={toggleSortOrder}
              disabled={files.length === 0}
            >
              <SortIcon className="text-primary size-5" />
            </Button>
          </div>
          <div className="flex items-center gap-5">
            <p className="text-sm text-[#667080]">
              {t('fileManager.transfer')} <br />
              {getFileCounts(files)} {t('fileManager.files')}
            </p>
            <Button
              disabled={!areThereAnyFiles || !anySelected}
              variant="outline"
              onClick={deleteSelectedFiles}
              type="button"
            >
              <TrashIcon className="text-primary size-[18px]" />
              <span className="hidden sm:block"> {t('delete')}</span>
            </Button>
          </div>
        </div>
        {files.length > 0 && (
          <div
            className={cn(
              'flex-1 space-y-4 overflow-y-auto',
              fileListClassName
            )}
          >
            {sortedFiles.map(fileItem => (
              <FileUploadItem
                key={fileItem.id}
                fileItem={fileItem}
                onUpdate={updateFile}
                onRemove={removeFile}
                loading={areFilesUploading}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
